<template>
  <div class="login-page">
    <div class="login-content">
      <div class="login-form">
        <div class="form-header">
          <div class="form-logo">
            <img src="/logo.png" alt="Ikhwan Food" />
          </div>
          <h2 class="form-title">Connexion</h2>
          <p class="form-subtitle">Connectez-vous à votre compte</p>
        </div>

        <div class="form-body">
          <div class="form-group" v-if="savedAccountInfo">
            <label class="form-label">
              <i class="bi bi-person"></i>
              Nom complet
            </label>
            <input
              type="text"
              :value="
                savedAccountInfo.fullName || savedAccountInfo.full_name || ''
              "
              class="form-input"
              readonly
              disabled
            />
          </div>

          <div class="form-group" v-if="savedAccountInfo">
            <label class="form-label">
              <i class="bi bi-person-badge"></i>
              Nom d'utilisateur
            </label>
            <input
              type="text"
              :value="savedAccountInfo.username || ''"
              class="form-input"
              readonly
              disabled
            />
          </div>

          <div class="form-group" v-if="!savedAccountInfo">
            <label for="username" class="form-label">
              <i class="bi bi-person"></i>
              Nom d'utilisateur
            </label>
            <input
              id="username"
              type="text"
              v-model="username"
              class="form-input"
              placeholder="Entrez votre nom d'utilisateur"
              @keyup.enter="handleLogin"
              :class="{ 'has-error': errors.username || usernameError }"
            />
            <p v-if="errors.username || usernameError" class="error-text">
              {{ errors.username || usernameError }}
            </p>
          </div>

          <div class="form-group">
            <label for="password" class="form-label">
              <i class="bi bi-lock"></i>
              Mot de passe
            </label>
            <div class="password-input-wrapper">
              <input
                id="password"
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                class="form-input"
                placeholder="Entrez votre mot de passe"
                @keyup.enter="handleLogin"
                :class="{ 'has-error': errors.password || passwordError }"
              />
              <button
                type="button"
                class="btn btn-toggle-password"
                @click="showPassword = !showPassword"
                tabindex="-1"
              >
                <i v-if="showPassword" class="bi bi-eye-slash-fill"></i>
                <i v-else class="bi bi-eye-fill"></i>
              </button>
            </div>
            <p v-if="errors.password || passwordError" class="error-text">
              {{ errors.password || passwordError }}
            </p>
          </div>

          <button
            v-if="savedAccountInfo"
            type="button"
            @click="clearSavedAccount"
            class="btn btn-secondary btn-clear-account"
          >
            <i class="bi bi-arrow-repeat"></i>
            <span>Changer d'utilisateur</span>
          </button>

          <button
            type="button"
            @click="handleLogin"
            class="btn btn-primary btn-login"
            :disabled="(!savedAccountInfo && !username) || !password"
          >
            <span class="btn-content">
              <span>Se connecter</span>
              <i class="bi bi-arrow-right"></i>
            </span>
          </button>

          <div v-if="error" class="error-message">
            <i class="bi bi-exclamation-triangle"></i>
            {{ error }}
          </div>
        </div>
      </div>
    </div>
    <div class="text-center footer-copyright">
      <br>
      © {{ currentYear }} <a href="www.peascom.ma">Peascom</a>, Tous les droits sont réservés
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "Login",
  data() {
    return {
      username: "",
      password: "",
      showPassword: false,
      isLoggingIn: false,
      errors: {},
      deviceIp: null,
      deviceInfo: null,
      savedAccountInfo: null,
    };
  },
  mounted() {
    this.loadSavedAccountInfo();
    this.getDeviceInfo();
    this.getDeviceIp();
  },
  methods: {
    loadSavedAccountInfo() {
      try {
        const savedInfo = localStorage.getItem("saved-account-info");
        if (savedInfo) {
          this.savedAccountInfo = JSON.parse(savedInfo);
          // Auto-populate username from saved info for login
          if (this.savedAccountInfo.username) {
            this.username = this.savedAccountInfo.username;
          }
        } else {
          this.savedAccountInfo = null;
        }
      } catch (error) {
        console.error("Error loading saved account info:", error);
        this.savedAccountInfo = null;
        localStorage.removeItem("saved-account-info");
      }
    },
    saveAccountInfo(user) {
      try {
        const accountInfo = {
          username: user.username || this.username,
          fullName: user.fullName || user.full_name || user.name || "",
        };
        localStorage.setItem("saved-account-info", JSON.stringify(accountInfo));
        this.savedAccountInfo = accountInfo;
      } catch (error) {
        console.error("Error saving account info:", error);
      }
    },
    clearSavedAccount() {
      localStorage.removeItem("saved-account-info");
      this.savedAccountInfo = null;
      this.username = "";
      this.password = "";
      this.errors = {};
    },
    getDeviceInfo() {
      const navigator = window.navigator;
      this.deviceInfo = {
        userAgent: navigator.userAgent || "Unknown",
        platform: navigator.platform || "Unknown",
        language: navigator.language || "Unknown",
        vendor: navigator.vendor || "Unknown",
        screenWidth: (window.screen && window.screen.width) || 0,
        screenHeight: (window.screen && window.screen.height) || 0,
        screenColorDepth: (window.screen && window.screen.colorDepth) || 0,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown",
        cookieEnabled: navigator.cookieEnabled || false,
        onLine: navigator.onLine || false,
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
        deviceMemory: navigator.deviceMemory || 0,
      };
    },
    async getDeviceIp() {
      try {
        // Helper function to fetch with timeout
        const fetchWithTimeout = (url, timeout = 5000) => {
          return Promise.race([
            fetch(url),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Timeout")), timeout)
            ),
          ]);
        };

        // Try multiple IP detection services as fallback
        const ipServices = [
          {
            url: "https://api.ipify.org?format=json",
            parser: (data) => data.ip,
          },
          {
            url: "https://api.ip.sb/ip",
            parser: (data) =>
              typeof data === "string" ? data.trim() : data.ip,
          },
          { url: "https://api.myip.com", parser: (data) => data.ip },
          { url: "https://ipapi.co/json/", parser: (data) => data.ip },
        ];

        for (const service of ipServices) {
          try {
            const response = await fetchWithTimeout(service.url, 5000);
            if (!response.ok) continue;

            let data;
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
              data = await response.json();
            } else {
              const text = await response.text();
              // Try to parse as JSON, otherwise use as string
              try {
                data = JSON.parse(text);
              } catch {
                data = text;
              }
            }

            // Use the parser function or default logic
            this.deviceIp = service.parser
              ? service.parser(data)
              : data.ip || data.query || data || null;

            if (
              this.deviceIp &&
              this.deviceIp !== "Unknown" &&
              this.deviceIp.trim() !== ""
            ) {
              break;
            }
          } catch (error) {
            console.warn(
              `Failed to get IP from ${service.url}:`,
              error.message
            );
            continue;
          }
        }

        // Fallback to "Unknown" if all services fail
        if (!this.deviceIp || this.deviceIp === "") {
          this.deviceIp = "Unknown";
        }
      } catch (error) {
        console.error("Error getting device IP:", error);
        this.deviceIp = "Unknown";
      }
    },
    validate() {
      this.errors = {};

      // If saved account info exists, use that username, otherwise validate input
      const usernameToValidate = this.savedAccountInfo
        ? this.savedAccountInfo.username
        : this.username;

      if (!usernameToValidate || !usernameToValidate.trim()) {
        this.errors.username = "Le nom d'utilisateur est requis.";
      } else {
        const trimmed = usernameToValidate.trim();
        const isUsername = /^[a-zA-Z0-9_@.-]+$/.test(trimmed);
        
        if (!isUsername) {
          this.errors.username = "Veuillez entrer un nom d'utilisateur valide.";
        }
      }

      if (!this.password || !this.password.trim()) {
        this.errors.password = "Le mot de passe est requis.";
      }

      return Object.keys(this.errors).length === 0;
    },
    async handleLogin() {
      // Prevent multiple simultaneous login requests
      if (this.isLoggingIn) {
        return;
      }

      // Validate inputs
      if (!this.validate()) {
        return;
      }

      // Attempt login with error handling
      try {
        this.isLoggingIn = true;
        this.errors = {};
        // Clear store errors
        this.$store.commit("auth/SET_ERROR", "");
        this.$store.commit("auth/SET_USERNAME_ERROR", "");
        this.$store.commit("auth/SET_PASSWORD_ERROR", "");

        // Use username from saved account info or input
        const usernameToUse = this.savedAccountInfo
          ? this.savedAccountInfo.username
          : this.username.trim();

        // Use username for login with device info
        const loginResponse = await this.$store.dispatch("auth/login", {
          username: usernameToUse,
          password: this.password.trim(),
          ip_address: this.deviceIp || "Unknown",
          device_info: this.deviceInfo || {},
        });

        // Save account info after successful login
        if (loginResponse && loginResponse.data && loginResponse.data.user) {
          this.saveAccountInfo(loginResponse.data.user);
        } else {
          // Fallback: get user from store
          const user = this.$store.getters["auth/user"];
          if (user && (user.id || user.reference)) {
            this.saveAccountInfo(user);
          }
        }

        // If login succeeds, router will navigate away automatically
        // No need to reset isLoggingIn as component will be destroyed
      } catch (error) {
        console.error("Login error:", error);
        this.isLoggingIn = false;

        const storeError = this.$store.getters["auth/getError"];
        const storeUsernameErr = this.$store.getters["auth/getUsernaneError"];
        const storePasswordErr = this.$store.getters["auth/getPasswordError"];

        if (storeError || storeUsernameErr || storePasswordErr) {
          this.errors = {};
        } else {
          const errorMessage =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            "Nom d'utilisateur ou mot de passe incorrect. Veuillez réessayer.";
          this.errors.password = errorMessage;
        }

        if (!storeUsernameErr) {
          this.password = "";
        }
      }
    },
  },
  computed: {
    ...mapGetters("auth", {
      usernameError: "getUsernaneError",
      passwordError: "getPasswordError",
      error: "getError",
    }),
    currentYear() {
      return new Date().getFullYear();
    },
  },
  created() {
    try {
      if (this.$route.query.username && this.$route.query.password) {
        const username = this.$route.query.username;
        const password = this.$route.query.password;
        this.username = username;
        this.password = password;
        this.handleLogin();
      }
    } catch (e) {
      console.log(e.message);
    }
  },
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: #ffffff;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.login-content {
  max-width: 500px;
  margin: 0 auto;
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-form {
  background: #ffffff;
  border-radius: 24px;
  padding: 3rem 2.5rem;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
}

.form-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.form-logo {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  border-radius: 20px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  padding: 1rem;
  box-shadow: 0 10px 30px rgba(99, 102, 241, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.form-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.form-title {
  font-size: 2rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 0.5rem;
  letter-spacing: -0.02em;
}

.form-subtitle {
  font-size: 1rem;
  color: #64748b;
  margin: 0;
}

.form-body {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-label i {
  color: #6366f1;
  font-size: 1rem;
}

.form-input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  color: #1e293b;
  background: #ffffff;
  transition: all 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
  opacity: 0.7;
}

.form-input.has-error {
  border-color: #ef4444;
}

.form-input.has-error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper .form-input {
  padding-right: 3rem;
}

.btn-toggle-password {
  position: absolute;
  right: 0.75rem;
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  z-index: 1;
}

.btn-toggle-password:hover {
  color: #6366f1;
}

.error-text {
  font-size: 0.875rem;
  color: #ef4444;
  margin: 0;
}

.error-message {
  margin-top: 1rem;
  padding: 0.875rem 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  color: #dc2626;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error-message i {
  font-size: 1rem;
  flex-shrink: 0;
}

.btn-login {
  width: 100%;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  box-shadow: 0 10px 30px rgba(99, 102, 241, 0.3);
  margin-top: 0.5rem;
}

.btn-login:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 15px 40px rgba(99, 102, 241, 0.4);
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
}

.btn-login:active:not(:disabled) {
  transform: translateY(0);
}

.btn-login:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-clear-account {
  width: 100%;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;
  color: #64748b;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-clear-account:hover {
  background: #e2e8f0;
  color: #475569;
  border-color: #cbd5e1;
}

.form-input:disabled {
  background: #f8fafc;
  color: #64748b;
  cursor: not-allowed;
  opacity: 0.8;
}

.btn-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-content i {
  font-size: 1.1rem;
  transition: transform 0.3s ease;
}

.btn-login:hover:not(:disabled) .btn-content i {
  transform: translateX(4px);
}

.footer-copyright {
  margin-top: 2rem;
  color: #64748b;
  font-size: 0.875rem;
}

.footer-copyright a {
  color: #6366f1;
  text-decoration: none;
}

.footer-copyright a:hover {
  text-decoration: underline;
}

@media screen and (max-width: 768px) {
  .login-page {
    padding: 1rem;
  }

  .login-form {
    padding: 2rem 1.5rem;
  }

  .form-title {
    font-size: 1.75rem;
  }

  .form-logo {
    width: 70px;
    height: 70px;
  }
}

@media screen and (max-width: 480px) {
  .login-form {
    padding: 1.5rem 1.25rem;
  }

  .form-title {
    font-size: 1.5rem;
  }
}
</style>




